'use client';
import { Container, Box, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { color, spacing } from 'src/theme/tokens';
import { typography as dsTypography } from 'src/theme/typography';
import { useTranslations } from 'next-intl';
import { Link } from 'src/i18n/routing';

const FooterRoot = styled.footer`
    background: ${color.gray[60]};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: auto;
`;

const Smallprint = styled(Typography)`
    font-weight: 400;
    font-size: 15px;
    line-height: 1.3;
    color: ${color.white[0]};
`;

const DisclaimerText = styled(Typography)`
    font-weight: 400;
    font-size: 14px;
    line-height: 1.5;
    color: ${color.white[0]};
` as typeof Typography;

const TermsLink = styled('span')(() => ({
    ...dsTypography.label5medium,
    color: color.white[0],
}));

const LinkStyled = styled(Link)`
    color: ${color.white[0]} !important;
    text-decoration: none;
    white-space: nowrap;
    &:hover {
        text-decoration: underline !important;
    }
    &:visited {
        color: ${color.white[0]};
    }
`;

const InlineLink = styled(Link)`
    color: ${color.white[0]} !important;
    text-decoration: underline;
    &:visited {
        color: ${color.white[0]};
    }
`;

function GGAutosalesPageFooter() {
    const t = useTranslations('GGAutosalesPage');
    return (
        <FooterRoot role="contentinfo" data-surface="footer">
            <Container>
                <Box
                    display="flex"
                    flexDirection="column"
                    px={0}
                    py={{ xs: spacing[24], sm: spacing[32], md: spacing[40] }}
                    maxWidth="1392px"
                    mx="auto"
                    gap={spacing[32]}
                >
                    <Box width="100%" display="flex" flexDirection="column" alignItems="flex-start" gap={spacing[4]}>
                        <Smallprint>
                            © {new Date().getFullYear()} {t('pageFooter.copyright')}{' '}
                            <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                                {t('pageFooter.allRightsReserved')}
                            </Box>
                        </Smallprint>
                        <Typography variant="label5regular" color={color.white[70]}>
                            {t('pageFooter.note')}
                        </Typography>
                    </Box>

                    <Box
                        width="100%"
                        pt={spacing[16]}
                        sx={{ borderTop: `1px solid ${color.white[20]}` }}
                        display="flex"
                        flexDirection={{ xs: 'column-reverse', sm: 'row' }}
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={{ xs: spacing[16], sm: 0 }}
                    >
                        <DisclaimerText component="div" sx={{ flex: { sm: '1 0 0' } }}>
                            <Box sx={{ maxWidth: { sm: '780px' } }}>
                                <Box component="span" fontWeight={600}>
                                    {t('pageFooter.disclaimerLabel')}
                                </Box>{' '}
                                {t('pageFooter.disclaimerText1')}{' '}
                                <InlineLink href="/gg-autosales-terms">{t('pageFooter.termsOfUse')}</InlineLink>
                                {t('pageFooter.disclaimerText2')}
                            </Box>
                            <Box>{t('pageFooter.disclaimerText3')}</Box>
                        </DisclaimerText>
                        <Box pl={{ xs: 0, sm: spacing[80] }}>
                            <LinkStyled href="/gg-autosales-terms">
                                <TermsLink>{t('pageFooter.terms')}</TermsLink>
                            </LinkStyled>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </FooterRoot>
    );
}

export default GGAutosalesPageFooter;
